import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import React, { useMemo, useState } from "react";
import {
  projectStatusMapping,
  ProjectStatusOption,
} from "../../utils/projectStatusMapping";
import DeletableChip from "../Layout/filters/DeletableChip";

interface VirtualizedProjectStatusDropdownProps {
  selectedStatuses: string[];
  onStatusesChange: (statuses: string[]) => void;
  label?: string;
}

const VirtualizedProjectStatusDropdown: React.FC<
  VirtualizedProjectStatusDropdownProps
> = ({ selectedStatuses, onStatusesChange, label = "Project Status" }) => {
  const [inputValue, setInputValue] = useState("");

  // Filter options based on search input
  const filteredOptions = useMemo(() => {
    return projectStatusMapping.searchByName(inputValue);
  }, [inputValue]);

  // Convert selected status IDs to option objects
  const selectedOptions = useMemo(() => {
    return selectedStatuses
      .map((id) => projectStatusMapping.getById(id))
      .filter((option): option is ProjectStatusOption => option !== undefined);
  }, [selectedStatuses]);

  const handleChange = (_: any, newValue: ProjectStatusOption[]) => {
    const newStatusIds = newValue.map((option) => option.id);
    onStatusesChange(newStatusIds);
  };

  const handleDelete = (statusToDelete: string) => {
    const newStatuses = selectedStatuses.filter(
      (status) => status !== statusToDelete
    );
    onStatusesChange(newStatuses);
  };

  return (
    <Box>
      <Autocomplete
        multiple
        options={filteredOptions}
        getOptionLabel={(option) => option.name}
        value={selectedOptions}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={
              selectedOptions.length === 0 ? "Search project statuses..." : ""
            }
            variant="outlined"
            size="small"
            InputLabelProps={{
              ...params.InputLabelProps,
              sx: {
                color: "#e0e0e0",
                "&.MuiInputLabel-shrink": {
                  transform: "translate(14px, -9px) scale(0.75)",
                },
              },
              shrink: selectedOptions.length > 0 || inputValue.length > 0,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#1e1e1e",
                "& fieldset": {
                  borderColor: "#555",
                },
                "&:hover fieldset": {
                  borderColor: "#777",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#1976d2",
                },
              },
              "& .MuiInputBase-input": {
                color: "#e0e0e0",
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#888888",
                opacity: 1,
              },
            }}
          />
        )}
        renderTags={(tagValue, getTagProps) => (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              paddingTop: "2px",
            }}
          >
            {tagValue.map((option, index) => {
              const { key, onDelete, ...otherTagProps } = getTagProps({
                index,
              });
              return (
                <DeletableChip
                  key={option.id}
                  label={option.name}
                  onDelete={() => handleDelete(option.id)}
                  {...otherTagProps}
                />
              );
            })}
          </Box>
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {option.name}
              </Typography>
            </Box>
          </li>
        )}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        filterOptions={(options) => options} // We handle filtering ourselves
        sx={{
          "& .MuiAutocomplete-popupIndicator": {
            color: "text.secondary",
          },
        }}
      />
    </Box>
  );
};

export default VirtualizedProjectStatusDropdown;
